import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { LangProvider } from './i18n';
import { AuthProvider } from './context/AuthContext';
import { SplashScreen } from './components/common/SplashScreen';
import { ProtectedRoute } from './components/common/ProtectedRoute';
import './App.css';

// Pages
import LandingPage from './components/pages/LandingPage';
import LoginPage from './components/pages/LoginPage';
import GoogleAuthCallback from './components/pages/GoogleAuthCallback';
import StaffLoginPage from './components/pages/StaffLoginPage';
import ServiceGallery from './components/pages/ServiceGallery';
import PublicQuotePage from './components/pages/PublicQuotePage';
import PaymentSuccess from './components/pages/PaymentSuccess';
import TicketTracker from './components/public/TicketTracker';

// Customer
import CustomerDashboard from './components/customer/CustomerDashboard';
import CustomerRequests from './components/customer/CustomerRequests';
import NewServiceRequest from './components/customer/NewServiceRequest';
import CustomerInvoices from './components/customer/CustomerInvoices';
import ServicesAndPricing from './components/customer/ServicesAndPricing';
import BlueprintBuilder from './components/customer/BlueprintBuilder';

// Technician
import TechnicianDashboard from './components/technician/TechnicianDashboard';
import TechnicianJobs from './components/technician/TechnicianJobs';
import TechnicianTimeTracking from './components/technician/TechnicianTimeTracking';

// Admin
import AdminDashboard from './components/admin/AdminDashboard';
import AdminRequests from './components/admin/AdminRequests';
import AdminLiveTracking from './components/admin/AdminLiveTracking';
import AdminTechnicians from './components/admin/AdminTechnicians';
import AdminPayroll from './components/admin/AdminPayroll';
import AdminCustomers from './components/admin/AdminCustomers';
import AdminGallery from './components/admin/AdminGallery';
import AdminSiteContent from './components/admin/AdminSiteContent';
import AdminSettings from './components/admin/AdminSettings';
import AdminInvoices from './components/admin/AdminInvoices';
import AdminFloorPlanBuilder from './components/admin/AdminFloorPlanBuilder';
import AdminEmailAnalytics from './components/admin/AdminEmailAnalytics';
import AdminDeliveryStatus from './components/admin/AdminDeliveryStatus';
import AdminBlueprintReader from './components/admin/AdminBlueprintReader';

function App() {
  const [showSplash, setShowSplash] = useState(true);
  const [hasSeenSplash, setHasSeenSplash] = useState(() => {
    return sessionStorage.getItem('hasSeenSplash') === 'true';
  });

  const handleSplashComplete = () => {
    setShowSplash(false);
    sessionStorage.setItem('hasSeenSplash', 'true');
    setHasSeenSplash(true);
  };

  // Google OAuth callback: process session_id BEFORE any routing/protected routes
  if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
    return <GoogleAuthCallback />;
  }

  const isDirectLink = typeof window !== 'undefined' && ['/quote', '/staff', '/login', '/track/'].some(p => window.location.pathname.startsWith(p));
  if (!hasSeenSplash && showSplash && !isDirectLink) {
    return <SplashScreen onComplete={handleSplashComplete} />;
  }

  return (
    <LangProvider>
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/staff" element={<StaffLoginPage />} />
          <Route path="/gallery/:serviceType" element={<ServiceGallery />} />
          <Route path="/quote" element={<PublicQuotePage />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/track/:ticket" element={<TicketTracker />} />

          {/* Customer Routes */}
          <Route path="/customer" element={<ProtectedRoute allowedRoles={['customer']}><CustomerDashboard /></ProtectedRoute>} />
          <Route path="/customer/services" element={<ProtectedRoute allowedRoles={['customer']}><ServicesAndPricing /></ProtectedRoute>} />
          <Route path="/customer/requests" element={<ProtectedRoute allowedRoles={['customer']}><CustomerRequests /></ProtectedRoute>} />
          <Route path="/customer/new-request" element={<ProtectedRoute allowedRoles={['customer']}><NewServiceRequest /></ProtectedRoute>} />
          <Route path="/customer/invoices" element={<ProtectedRoute allowedRoles={['customer']}><CustomerInvoices /></ProtectedRoute>} />
          <Route path="/customer/blueprint" element={<ProtectedRoute allowedRoles={['customer']}><BlueprintBuilder /></ProtectedRoute>} />

          {/* Technician Routes */}
          <Route path="/technician" element={<ProtectedRoute allowedRoles={['technician']}><TechnicianDashboard /></ProtectedRoute>} />
          <Route path="/technician/jobs" element={<ProtectedRoute allowedRoles={['technician']}><TechnicianJobs /></ProtectedRoute>} />
          <Route path="/technician/time" element={<ProtectedRoute allowedRoles={['technician']}><TechnicianTimeTracking /></ProtectedRoute>} />

          {/* Admin Routes */}
          <Route path="/admin" element={<ProtectedRoute allowedRoles={['admin']}><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/requests" element={<ProtectedRoute allowedRoles={['admin']}><AdminRequests /></ProtectedRoute>} />
          <Route path="/admin/tracking" element={<ProtectedRoute allowedRoles={['admin']}><AdminLiveTracking /></ProtectedRoute>} />
          <Route path="/admin/technicians" element={<ProtectedRoute allowedRoles={['admin']}><AdminTechnicians /></ProtectedRoute>} />
          <Route path="/admin/payroll" element={<ProtectedRoute allowedRoles={['admin']}><AdminPayroll /></ProtectedRoute>} />
          <Route path="/admin/customers" element={<ProtectedRoute allowedRoles={['admin']}><AdminCustomers /></ProtectedRoute>} />
          <Route path="/admin/gallery" element={<ProtectedRoute allowedRoles={['admin']}><AdminGallery /></ProtectedRoute>} />
          <Route path="/admin/site-content" element={<ProtectedRoute allowedRoles={['admin']}><AdminSiteContent /></ProtectedRoute>} />
          <Route path="/admin/settings" element={<ProtectedRoute allowedRoles={['admin']}><AdminSettings /></ProtectedRoute>} />
          <Route path="/admin/invoices" element={<ProtectedRoute allowedRoles={['admin']}><AdminInvoices /></ProtectedRoute>} />
          <Route path="/admin/blueprint" element={<ProtectedRoute allowedRoles={['admin']}><BlueprintBuilder /></ProtectedRoute>} />
          <Route path="/admin/floor-plan" element={<ProtectedRoute allowedRoles={['admin']}><AdminFloorPlanBuilder /></ProtectedRoute>} />
          <Route path="/admin/emails" element={<ProtectedRoute allowedRoles={['admin']}><AdminEmailAnalytics /></ProtectedRoute>} />
          <Route path="/admin/delivery" element={<ProtectedRoute allowedRoles={['admin']}><AdminDeliveryStatus /></ProtectedRoute>} />
          <Route path="/admin/blueprint-reader" element={<ProtectedRoute allowedRoles={['admin']}><AdminBlueprintReader /></ProtectedRoute>} />

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
    </LangProvider>
  );
}

export default App;
